import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { giftsApi, affiliateApi } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useToastActions } from "../components/shared/Toast";
import { buildGiftCardsUrl, buildRetailerLinks } from "../utils/retailerLinks";
import SavingNotice from "../components/shared/SavingNotice";
import useMinLoad from "../hooks/useMinLoad";
import useFormPresentation from "../hooks/useFormPresentation";

const CATEGORY_COLORS = {
  physical: ["#EEF2FF", "#6366F1"],
  experience: ["#EAF3EE", "#4A7C59"],
  digital: ["#FDF6E3", "#C9A84C"],
  consumable: ["#FDEEE9", "#E7614A"],
};

function GiftSheetHeader({ title, icon = "🎁", subtitle = "" }) {
  return (
    <>
      <div className="gf-sheet-logo-row">
        <div className="gf-sheet-brand">
          <div className="gf-sheet-logo-ico" aria-hidden="true">{icon}</div>
          <span className="gf-sheet-title">{title}</span>
        </div>
      </div>
      {subtitle && <div className="gf-sheet-subtitle">{subtitle}</div>}
    </>
  );
}

function PriceRange({ min, max }) {
  if (!min && !max) return null;
  const label = min && max ? `$${min}–$${max}` : max ? `~$${max}` : `$${min}+`;
  return (
    <span className="gift-suggest-price">
      {label}
    </span>
  );
}

function SuggestionCard({ suggestion, occasion, shoppingRegion, onSave, onUnsave, onPurchase, onUnpurchase, onAffiliateClick }) {
  const [saved, setSaved] = useState(suggestion.was_saved);
  const [purchased, setPurchased] = useState(suggestion.was_purchased);
  const [showPurchaseConfirm, setShowPurchaseConfirm] = useState(false);
  const [actualPrice, setActualPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState("");
  const showActionNotice = useMinLoad(busy, 500);
  const catColors = CATEGORY_COLORS[suggestion.category] || ["var(--cream)", "var(--ink-muted)"];

  const uniqueText = `${suggestion.category || ""} ${suggestion.title || ""} ${suggestion.description || ""} ${suggestion.why_it_fits || ""}`.toLowerCase();
  const isUnique = suggestion.category === "experience" || /(unique|personalized|custom|handmade|hard to shop)/.test(uniqueText);
  const isLastMinute = occasion ? (occasion.days_until != null && occasion.days_until <= 3) : false;

  const retailerLinks = buildRetailerLinks({
    title: suggestion.title,
    amazonSearchQuery: suggestion.amazon_search_query,
    etsySearchQuery: suggestion.etsy_search_query,
    amazonUrl: suggestion.amazon_url,
    etsyUrl: suggestion.etsy_url,
    shoppingRegion,
    isUnique,
    isLastMinute,
  });

  const handleSave = async () => {
    setBusy(true);
    setBusyMessage("Saving idea...");
    try { await onSave(suggestion.id); setSaved(true); } finally { setBusy(false); }
  };

  const handleUnsave = async () => {
    setBusy(true);
    setBusyMessage("Removing saved idea...");
    try { await onUnsave(suggestion.id); setSaved(false); } finally { setBusy(false); }
  };

  const handlePurchase = async (priceValue = "") => {
    setBusy(true);
    setBusyMessage("Marking purchased...");
    try {
      await onPurchase(suggestion.id, priceValue);
      setSaved(true);
      setPurchased(true);
      setShowPurchaseConfirm(false);
    } finally {
      setBusy(false);
    }
  };

  const handleUnpurchase = async () => {
    setBusy(true);
    setBusyMessage("Undoing purchase...");
    try { await onUnpurchase(suggestion.id); setPurchased(false); } finally { setBusy(false); }
  };

  return (
    <div className={`suggestion-card gift-suggest-card${saved ? " is-saved" : ""}${purchased ? " is-purchased" : ""}`}>
      <div className="gift-suggest-card-head">
        <span className="badge" style={{ background: catColors[0], color: catColors[1] }}>
          {suggestion.category}
        </span>
        <PriceRange min={suggestion.price_min} max={suggestion.price_max} />
      </div>
      <div className="suggestion-title">{suggestion.title}</div>
      <div className="suggestion-why">✦ {suggestion.why_it_fits}</div>

      <div className="gift-suggest-store-row" aria-label="Shop this gift idea">
        {retailerLinks.map(link => (
          <a
            key={link.key}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`affiliate-btn gift-store-btn ${link.className}`}
            onClick={() => {
              // Internal suggestion click tracking (giftsApi)
              onAffiliateClick(suggestion.id);
              // Affiliate program tracking (fire-and-forget)
              affiliateApi.track(link.program, "gifts", link.url).catch(() => {});
            }}
          >
            <span aria-hidden="true">🛒</span>
            {link.label}
          </a>
        ))}
      </div>

      <div className="gift-suggest-state-row">
        {/* ── State: not saved, not purchased ── */}
        {!saved && !purchased && (
          <>
            <button className="btn btn-secondary btn-sm gift-save-btn" onClick={handleSave} disabled={busy}>
              ♡ Save idea
            </button>
            <button
              className="btn btn-sm gift-purchase-btn"
              onClick={() => setShowPurchaseConfirm(true)}
              disabled={busy}
            >
              Mark purchased ✓
            </button>
          </>
        )}

        {/* ── State: saved, not yet purchased ── */}
        {saved && !purchased && (
          <>
            <button className="btn btn-secondary btn-sm gift-saved-pill" onClick={handleUnsave} disabled={busy}>
              ♥ Saved
            </button>
            <button
              className="btn btn-sm gift-purchase-btn"
              onClick={() => setShowPurchaseConfirm(true)}
              disabled={busy}
            >
              Mark purchased ✓
            </button>
          </>
        )}

        {/* ── State: purchased ── */}
        {purchased && (
          <div className="gift-purchased-bar">
            <span>✓ Marked as purchased</span>
            <button type="button" onClick={handleUnpurchase} disabled={busy}>Undo</button>
          </div>
        )}
      </div>
      {showPurchaseConfirm && !purchased && (
        <div className="gf-purchase-confirm">
          <div className="gf-purchase-confirm__label">How much did you spend?</div>
          <div className="gf-purchase-confirm__row">
            <input
              className="hm-input"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder={suggestion.price_min || suggestion.price_max || "0"}
              value={actualPrice}
              onChange={event => setActualPrice(event.target.value)}
            />
            <button
              type="button"
              className="hm-btn hm-btn--primary"
              onClick={() => handlePurchase(actualPrice)}
              disabled={busy}
            >
              Confirm
            </button>
          </div>
          <div className="gf-purchase-confirm__actions">
            <button type="button" onClick={() => handlePurchase("")} disabled={busy}>
              Skip - use estimate
            </button>
            <button type="button" onClick={() => setShowPurchaseConfirm(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {showActionNotice && <SavingNotice title="Updating" message={busyMessage || "Updating gift idea..."} />}
    </div>
  );
}

function avatarInitials(name = "?") {
  return name.split(" ").map(part => part[0]).join("").slice(0, 2).toUpperCase();
}

function occasionLabel(type = "occasion") {
  return type.replace(/_/g, " ");
}

export default function GiftSuggestPage() {
  const { occasionId } = useParams();
  const navigate = useNavigate();
  const presentation = useFormPresentation();
  const { user, shoppingRegion, applyAiUsage } = useAuth();
  const toastActions = useToastActions();

  const [occasion, setOccasion] = useState(null);
  const [person, setPerson] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cardMessage, setCardMessage] = useState(null);
  const [cardTone, setCardTone] = useState("warm");
  const [showCardPrompt, setShowCardPrompt] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [showCardSheet, setShowCardSheet] = useState(false);
  const cardSheetRef = useRef(null);
  const cardSheetDragRef = useRef({ active: false, startY: 0, currentY: 0 });
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState(user?.ai_provider || "openai");
  const occasionType = (occasion?.occasion_type || "").toLowerCase();
  const isWeddingOccasion = occasionType === "wedding";
  const giftPreferenceText = (person?.gift_preferences || "").toLowerCase();
  const prefersGiftCards = /gift\s*cards?|giftcards?|card/.test(giftPreferenceText);
  const giftCardUrl = buildGiftCardsUrl(person?.name ? `${person.name} gift card` : "gift card");
  const zolaUrl = "https://www.zola.com/wedding-registry?utm_source=housemind&utm_medium=app&utm_campaign=wedding";
  const showGiftLoading = useMinLoad(loading, 700);
  const showCardLoading = useMinLoad(cardLoading, 700);
  const canConsumeAI = !user?.ai_requests_limit || (user?.ai_requests_used || 0) < user.ai_requests_limit;
  const [activeCategory, setActiveCategory] = useState("all");
  const categoryFilters = useMemo(() => {
    const labels = suggestions
      .map(item => String(item.category || "").trim())
      .filter(Boolean);
    return ["all", ...Array.from(new Set(labels)).slice(0, 6)];
  }, [suggestions]);
  const visibleSuggestions = useMemo(() => (
    activeCategory === "all"
      ? suggestions
      : suggestions.filter(item => String(item.category || "").toLowerCase() === activeCategory.toLowerCase())
  ), [activeCategory, suggestions]);

  useEffect(() => {
    if (activeCategory === "all") return;
    const hasCategory = suggestions.some(item => String(item.category || "").toLowerCase() === activeCategory.toLowerCase());
    if (!hasCategory) setActiveCategory("all");
  }, [activeCategory, suggestions]);

  // Load occasion context and any past suggestions on mount.
  useEffect(() => {
    let active = true;

    Promise.all([
      giftsApi.occasionContext(occasionId).catch(() => null),
      giftsApi.getHistory(occasionId).catch(() => []),
    ]).then(([context, history]) => {
      if (!active) return;
      if (context?.person) setPerson(context.person);
      if (context?.occasion) setOccasion(context.occasion);
      if (history.length > 0) setSuggestions(history);
    });

    return () => {
      active = false;
    };
  }, [occasionId]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await giftsApi.suggest(occasionId, { provider });
      setSuggestions(data.suggestions);
      setOccasion(data.occasion);
      setPerson(data.person);
      applyAiUsage(data.ai_usage);
      toastActions?.addToast?.("Gift ideas ready", "success");
    } catch (e) {
      if (e.data?.ai_usage) applyAiUsage(e.data.ai_usage);
      const message = e.data?.upgrade_required
        ? "You've used your free AI suggestions. Upgrade to keep getting personalized ideas and planning tools."
        : e.message;
      setError(message);
      toastActions?.addToast?.(message || "Could not generate gift ideas", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await giftsApi.suggest(occasionId, { provider });
      setSuggestions(prev => {
        const seen = new Set(prev.map(item => item.id));
        return [...(data.suggestions || []).filter(item => !seen.has(item.id)), ...prev];
      });
      setOccasion(data.occasion);
      setPerson(data.person);
      setActiveCategory("all");
      applyAiUsage(data.ai_usage);
      toastActions?.addToast?.("More gift ideas added", "success");
    } catch (e) {
      if (e.data?.ai_usage) applyAiUsage(e.data.ai_usage);
      const message = e.data?.upgrade_required
        ? "You've used your free AI suggestions. Upgrade to keep getting personalized ideas and planning tools."
        : e.message;
      setError(message);
      toastActions?.addToast?.(message || "Could not find more gift ideas", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCardMessage = async (tone = cardTone) => {
    setCardTone(tone);
    setCardLoading(true);
    setError(null);
    try {
      const data = await giftsApi.getCardMessage(occasionId, { provider, tone });
      setCardMessage(data.message);
      setShowCardPrompt(false);
      setShowCardSheet(false);
      applyAiUsage(data.ai_usage);
      toastActions?.addToast?.("Card message ready", "success");
    } catch (e) {
      if (e.data?.ai_usage) applyAiUsage(e.data.ai_usage);
      const message = e.data?.upgrade_required
        ? "You've used your free AI suggestions. Upgrade to keep getting personalized ideas and planning tools."
        : e.message;
      setError(message);
      toastActions?.addToast?.(message || "Could not write card message", "error");
    } finally {
      setCardLoading(false);
    }
  };

  const handlePurchaseComplete = async (suggestionId, actualPrice = "") => {
    await giftsApi.markPurchased(suggestionId, actualPrice ? { actual_price: actualPrice } : {});
    setShowCardPrompt(true);
    setCardMessage(null);
  };

  const closeCardSheet = useCallback(() => {
    if (cardSheetRef.current) {
      cardSheetRef.current.style.transform = "";
      cardSheetRef.current.style.transition = "";
    }
    setShowCardSheet(false);
  }, []);

  const startCardSheetDrag = useCallback((event) => {
    if (!showCardSheet) return;
    cardSheetDragRef.current = { active: true, startY: event.clientY, currentY: 0 };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (cardSheetRef.current) cardSheetRef.current.style.transition = "none";
  }, [showCardSheet]);

  const moveCardSheetDrag = useCallback((event) => {
    if (!cardSheetDragRef.current.active || !cardSheetRef.current) return;
    const delta = Math.max(0, event.clientY - cardSheetDragRef.current.startY);
    cardSheetDragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    cardSheetRef.current.style.transform = `translateY(${delta}px)`;
  }, []);

  const endCardSheetDrag = useCallback((event) => {
    if (!cardSheetDragRef.current.active || !cardSheetRef.current) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const delta = cardSheetDragRef.current.currentY;
    cardSheetDragRef.current = { active: false, startY: 0, currentY: 0 };
    if (delta > 72) {
      cardSheetRef.current.style.transition = "transform .2s cubic-bezier(0.4, 0, 1, 1)";
      cardSheetRef.current.style.transform = "translateY(100%)";
      window.setTimeout(closeCardSheet, 205);
      return;
    }
    cardSheetRef.current.style.transition = "transform .18s cubic-bezier(0.2, 0, 0, 1)";
    cardSheetRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!cardSheetRef.current || cardSheetDragRef.current.active) return;
      cardSheetRef.current.style.transform = "";
      cardSheetRef.current.style.transition = "";
    }, 190);
  }, [closeCardSheet]);

  const pageContent = (
    <div className="gift-suggest-page hm-desktop-page-shell">
      <div className="flex-between mb-16 gift-suggest-topline">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          ← {person?.name || "Back"}
        </button>
        {user?.is_admin && (
          <div className="flex-center gap-8 gift-provider-control">
            <span className="text-sm text-muted">AI provider:</span>
            <select
              className="form-select"
              style={{ width: "auto", padding: "6px 12px", fontSize: 13 }}
              value={provider}
              onChange={e => setProvider(e.target.value)}
            >
              <option value="anthropic">Claude (Anthropic)</option>
              <option value="openai">OpenAI (gpt-4o-mini)</option>
            </select>
          </div>
        )}
      </div>

      {person && occasion && (
        <div className="gift-context-bar">
          <div className="gift-context-avatar">{avatarInitials(person.name)}</div>
          <div className="gift-context-copy">
            <strong>{person.name}</strong>
            <span>
              {occasionLabel(occasion.occasion_type)} · {occasion.days_until === 0 ? "today" : `${occasion.days_until} days`} · ${person.budget_min}–${person.budget_max}
            </span>
          </div>
        </div>
      )}

      {!person && (
        <div className="page-header">
          <h2>Gift suggestions</h2>
          <p>Generate personalized AI gift ideas</p>
        </div>
      )}

      {error && (
        <div className="card mb-16" style={{ borderColor: "var(--rose)", background: "var(--rose-light)" }}>
          <p style={{ color: "var(--rose-dark)", fontSize: 14 }}>{error}</p>
        </div>
      )}

      {(user?.plan === "family" || user?.workspace?.plan === "family") && (
        <div className="notice-banner mb-16">
          <p>
            Gift planning is private to your account. Family members only see gift content if you choose to share it later.
          </p>
        </div>
      )}

      <div className="flex-center gap-12 mb-16 gift-suggest-actions">
        <button
          className="btn btn-primary btn-lg"
          onClick={handleGenerate}
          disabled={loading}
          style={{ minWidth: 200 }}
        >
          {suggestions.length > 0 ? "Regenerate ideas" : "✦ Get gift ideas"}
        </button>
        {person && (
          <button
            className="btn btn-secondary"
            onClick={() => setShowCardSheet(true)}
            disabled={cardLoading}
          >
            💌 Card message
          </button>
        )}
      </div>

      {categoryFilters.length > 1 && (
        <div className="gift-filter-row" aria-label="Gift categories">
          {categoryFilters.map(category => (
            <button
              key={category}
              type="button"
              className={activeCategory === category ? "is-active" : ""}
              onClick={() => setActiveCategory(category)}
            >
              {category === "all" ? "All" : category}
            </button>
          ))}
        </div>
      )}

      {cardMessage && (
        <div className="gf-card-result">
          <div className="gf-card-result__label">✦ Card message · {cardTone}</div>
          <div className="gf-card-result__text">{cardMessage}</div>
          <div className="gf-card-result__actions">
            <button
              type="button"
              className="gf-card-copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(cardMessage);
                toastActions?.addToast?.("Copied to clipboard", "success");
              }}
            >
              Copy
            </button>
            <button type="button" className="gf-card-regen-btn" onClick={() => handleCardMessage(cardTone)} disabled={cardLoading}>
              Try again
            </button>
          </div>
        </div>
      )}

      {suggestions.length > 0 && (
        <>
          <div className="suggestions-grid">
            {visibleSuggestions.map(s => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                occasion={occasion}
                shoppingRegion={shoppingRegion}
                onSave={giftsApi.save}
                onUnsave={giftsApi.unsave}
                onPurchase={handlePurchaseComplete}
                onUnpurchase={giftsApi.unmarkPurchased}
                onAffiliateClick={giftsApi.trackClick}
              />
            ))}
          </div>
          {showCardPrompt && !cardMessage && (
            <div className="gf-card-prompt">
              <div className="gf-card-prompt__ico">✉</div>
              <div className="gf-card-prompt__body">
                <div className="gf-card-prompt__title">Write a card message?</div>
                <div className="gf-card-prompt__sub">
                  HouseMind can write something personal for {person?.name}'s {occasionLabel(occasion?.occasion_type)}.
                </div>
                <div className="gf-card-prompt__tones">
                  {["warm", "funny", "heartfelt", "short"].map(tone => (
                    <button
                      key={tone}
                      type="button"
                      className={`gf-tone-pill${cardTone === tone ? " gf-tone-pill--on" : ""}`}
                      onClick={() => setCardTone(tone)}
                    >
                      {tone}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="hm-btn hm-btn--primary gf-card-generate-btn"
                  disabled={cardLoading || !canConsumeAI}
                  onClick={() => handleCardMessage(cardTone)}
                >
                  {cardLoading ? "Writing..." : "✦ Generate message"}
                </button>
              </div>
              <button type="button" className="gf-card-prompt__skip" onClick={() => setShowCardPrompt(false)}>
                Skip
              </button>
            </div>
          )}
          <button
            type="button"
            className="gf-regenerate-btn"
            disabled={loading || !canConsumeAI}
            onClick={handleRegenerate}
          >
            {loading ? "Finding ideas..." : "Try different ideas"}
          </button>
          <p className="affiliate-disclosure">
            Some links above may be affiliate links. HouseMind may earn a small commission if you purchase through them — at no extra cost to you. This helps keep the app free.
          </p>
        </>
      )}

      {person && occasion && suggestions.length > 0 && (
        <div className="gift-flex-card">
          <div>
            <div className="gift-flex-eyebrow">
              {prefersGiftCards ? "Gift card preference" : "Flexible gift cards"}
            </div>
            <p>
              {prefersGiftCards
                ? "Keep a gift card visible because this person may prefer flexibility."
                : "Use this when timing is tight or none of the suggestions feels quite right."}
            </p>
          </div>
          <div className="flex-center gap-8" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
            <a
              href={giftCardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="affiliate-btn gift-store-btn affiliate-giftcards"
              onClick={() => affiliateApi.track("giftcards_com", "gifts", giftCardUrl).catch(() => {})}
            >
              <span aria-hidden="true">🛒</span>
              Gift Cards
            </a>
            {isWeddingOccasion && (
              <a
                href={zolaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="affiliate-btn gift-store-btn affiliate-uncommongoods"
                onClick={() => affiliateApi.track("zola", "gifts", zolaUrl).catch(() => {})}
              >
                <span aria-hidden="true">🛒</span>
                Zola registry
              </a>
            )}
          </div>
        </div>
      )}

      {suggestions.length === 0 && !loading && (
        <div className="gift-suggest-empty-state">
          {person && occasion ? (
            <>
              <div className="gift-suggest-empty-preview">
                <div className="gift-suggest-empty-avatar">{avatarInitials(person.name)}</div>
                <div className="gift-suggest-empty-meta">
                  <strong>{person.name}</strong>
                  <span>
                    {occasion.occasion_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                    {occasion.days_until != null ? ` · ${occasion.days_until === 0 ? "today" : occasion.days_until === 1 ? "tomorrow" : `${occasion.days_until} days away`}` : ""}
                  </span>
                  <span>Budget ${person.budget_min}–${person.budget_max}</span>
                  {(person.interest_tags?.length > 0 || person.hobby_tags?.length > 0) && (
                    <div className="gift-suggest-empty-tags">
                      {[...(person.interest_tags || []), ...(person.hobby_tags || [])].slice(0, 4).map(tag => (
                        <em key={tag}>{tag}</em>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <p className="gift-suggest-empty-hint">
                HouseMind will use {person.name}'s interests, budget, and occasion context to generate personalised gift ideas. Hit the button above to get started.
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✦</div>
              <p>Generate personalised AI gift ideas for this occasion.</p>
            </>
          )}
        </div>
      )}

      {showGiftLoading && <SavingNotice title="Finding" message="Finding gift ideas..." />}
      {showCardLoading && <SavingNotice title="Writing" message="Writing card message..." />}
      {showCardSheet && (
        <div className="hm-gifts-sheet-overlay" onClick={closeCardSheet}>
          <div className="hm-gifts-action-sheet gift-card-message-sheet" ref={cardSheetRef} onClick={event => event.stopPropagation()}>
            <div className="gf-sheet-top-row">
              <button
                type="button"
                className="gf-sheet-handle"
                onPointerDown={startCardSheetDrag}
                onPointerMove={moveCardSheetDrag}
                onPointerUp={endCardSheetDrag}
                onPointerCancel={endCardSheetDrag}
                onClick={closeCardSheet}
                aria-label="Drag down to close"
              />
              <button type="button" className="gf-sheet-top-close" onClick={closeCardSheet} aria-label="Close card message">×</button>
            </div>
            <GiftSheetHeader title="Card message" icon="💌" subtitle="Choose the tone HouseMind should write in." />
            <div className="hm-gifts-sheet-actions">
              {[
                ["warm", "💌", "Warm", "Personal, simple, and caring"],
                ["funny", "✨", "Funny", "Light, sweet, and not too much"],
                ["heartfelt", "♥", "Heartfelt", "A little more personal"],
                ["short", "✎", "Short", "Brief and polished"],
              ].map(([tone, icon, title, sub]) => (
                <button key={tone} type="button" onClick={() => handleCardMessage(tone)} disabled={cardLoading}>
                  <span>{icon}</span>
                  <span><strong>{title}</strong><small>{sub}</small></span>
                  <em>›</em>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (presentation === "sheet") {
    return (
      <div className="fd-clear-overlay gf-edit-cover-overlay gf-people-overlay gf-gift-ideas-overlay" onClick={() => navigate(-1)}>
        <div className="gf-edit-cover-sheet gf-people-sheet gf-gift-ideas-sheet" onClick={event => event.stopPropagation()}>
          <div className="gf-sheet-top-row">
            <button type="button" className="gf-sheet-handle" onClick={() => navigate(-1)} aria-label="Close gift ideas" />
            <button type="button" className="gf-sheet-top-close" onClick={() => navigate(-1)} aria-label="Close gift ideas">×</button>
          </div>
          <GiftSheetHeader title="Gift ideas" />
          {pageContent}
        </div>
      </div>
    );
  }

  return pageContent;
}
